import format from "string-format";
import { TaJson } from "ta-json";

import { IApiInfo } from "../api/api-info";
import { ApiInfoResource } from "../api/api-info-resource";
import { ApiRoutesDictionary } from "../api/api-routes-dictionary";
import { MapStringTo } from "../base-types";
import { API_ROOT, HEADERS } from "../constants/api";
import ErrorMessages from "../error-messages";
import { NotSupportedError } from "../errors/not-supported-error";
import { FileVersion } from "../fileversion";
import Guard from "../guard";
import Link from "../link";
import { ApiResourceMapper } from "../mappers/api-resource-mapper";
import { Compatibility } from "../models/versionining/compatibility";
import { IExtendedContentHubClient } from "./extended-client";
import { ResponseHandler } from "./response-handler";

/**
 * Contains functionality to get REST API information.
 * It will also check for compatibility with the server REST API.
 */
export interface IApiClient {
    /**
     * Gets or sets whether compatibility checks should be disabled.
     *
     * @remarks
     * The value is shared across all {@link IExtendedContentHubClient} instances.
     */
    disableCompatibilityChecks: boolean;

    /**
     * Gets the REST API information. The result will be cached.
     *
     * @returns The API information.
     */
    getApiInfoAsync(): Promise<IApiInfo>;

    /**
     * Gets the REST API routes. The result will be cached.
     *
     * @returns The REST API routes.
     */
    getApiRoutesAsync(): Promise<ApiRoutesDictionary>;

    /**
     * Forces a refresh of the cached REST API information.
     */
    refreshAsync(): Promise<void>;
}

export class ApiClient implements IApiClient {
    private readonly _client: IExtendedContentHubClient;
    private _api: IApiInfo;
    private static _disableCompatibilityChecks: boolean;

    get disableCompatibilityChecks(): boolean {
        return ApiClient._disableCompatibilityChecks;
    }
    set disableCompatibilityChecks(value: boolean) {
        ApiClient._disableCompatibilityChecks = value;
    }

    constructor(client: IExtendedContentHubClient) {
        Guard.notNullOrUndefined(client);

        this._client = client;
    }

    async getApiInfoAsync(): Promise<IApiInfo> {
        await this.loadAsync();

        return this._api;
    }

    async getApiRoutesAsync(): Promise<MapStringTo<Link>> {
        await this.loadAsync();

        return this._api.routes;
    }

    async refreshAsync(): Promise<void> {
        this._api = await this.fetchApiInfoAsync();
    }

    /**
     * Load the REST API information if absent.
     */
    private async loadAsync(): Promise<void> {
        if (!this._api) {
            await this.refreshAsync();
        }
    }

    /**
     * Fetches the API information and checks if the server is running a compatible Content Hub version.
     *
     * @returns The API information.
     */
    private async fetchApiInfoAsync(): Promise<IApiInfo> {
        const headers: MapStringTo<string> = {};
        headers[HEADERS.apiVersion] = "2";

        const response = await this._client.raw.getAsync(API_ROOT, headers);

        ResponseHandler.handleErrors(response);

        let resource: ApiInfoResource;

        try {
            resource = TaJson.deserialize<ApiInfoResource>(response && response.content, ApiInfoResource);

            if (
                resource === null ||
                resource.fileVersion === null ||
                resource.minimumSdkVersion === null ||
                resource.routes === null
            ) {
                throw 0;
            }
        } catch (ex) {
            throw new NotSupportedError(ErrorMessages.ContentHubClient.IncompatibleVersionsPre3_0);
        }

        const sdkVersion = new FileVersion(3, 0, 0); //TODO Load this from somewhere
        const serverVersion = resource.fileVersion;

        if (!this.disableCompatibilityChecks) {
            const compatibilityResult = this._client.versionChecker.getCompatibility(
                serverVersion,
                sdkVersion,
                resource.minimumSdkVersion
            );

            if (compatibilityResult == Compatibility.SdkOld) {
                throw new NotSupportedError(
                    format(
                        ErrorMessages.ContentHubClient.IncompatibleMinimumVersion,
                        sdkVersion,
                        serverVersion,
                        resource.minimumSdkVersion
                    )
                );
            }

            if (compatibilityResult == Compatibility.SdkNew) {
                throw new NotSupportedError(
                    format(ErrorMessages.ContentHubClient.IncompatibleVersion, sdkVersion, serverVersion)
                );
            }
        }

        const apiInfo = ApiResourceMapper.map(resource);

        return apiInfo;
    }
}
